import { disposeClusterPanelBootstrapCarousels } from './dispose-bootstrap-carousel';
import { initClusterPanelCarousels } from './init-cluster-about-operating-widgets';

const VALID_CLUSTERS = [
  'obrazovatelnyy',
  'lomonosov',
  'inzhiniring',
  'geotekh',
  'kosmos',
  'biomed',
  'infotekh',
  'nanotekh',
  'mezhdistsiplinarnyy',
];

const DEFAULT_CLUSTER = 'obrazovatelnyy';

let panelCleanup: (() => void) | null = null;
let listRoot: HTMLElement | null = null;
let globalEventsReady = false;

function isClusterId(value: string): boolean {
  return VALID_CLUSTERS.includes(value);
}

function parseCluster(): string {
  const params = window.location.searchParams
    ? window.location.searchParams
    : new URLSearchParams(window.location.search || '');
  const code = params.get('cluster')?.trim().toLowerCase();
  if (code && isClusterId(code)) return code;
  return DEFAULT_CLUSTER;
}

function clusterUrl(cluster: string): string {
  const url = new URL(window.location.href);
  url.searchParams.set('cluster', cluster);
  return `${url.pathname}${url.search}${url.hash}`;
}

function getPanel(cluster: string): HTMLElement | null {
  return document.querySelector<HTMLElement>(`.cluster-panel[data-cluster="${cluster}"]`);
}

function syncList(zoneId: string): void {
  document
    .querySelectorAll<HTMLAnchorElement>('[data-clusters-list] .clusters-list__link')
    .forEach((link) => {
      const active = link.dataset.zoneId === zoneId;
      link.classList.toggle('clusters-list__link--current', active);
      if (active) link.setAttribute('aria-current', 'page');
      else link.removeAttribute('aria-current');
    });
}

function syncResidentBtn(panel: HTMLElement): void {
  const id = panel.dataset.residentClusterId;
  if (!id) return;
  const main = document.querySelector<HTMLAnchorElement>('.clusters-hub__resident');
  if (main) main.dataset.cluster = id;
  const disabled = document.querySelector<HTMLAnchorElement>('.cluster-button-block .btn.disabled');
  if (disabled) disabled.dataset.cluster = id;
}

export function showCluster(cluster: string, opts?: { push?: boolean; replace?: boolean }): void {
  const panel = getPanel(cluster);
  if (!panel) return;

  const zoneId = panel.dataset.zoneId || cluster;

  const panelsRoot = document.querySelector('[data-clusters-panels]');
  const panels = panelsRoot
    ? panelsRoot.querySelectorAll<HTMLElement>('.cluster-panel')
    : document.querySelectorAll<HTMLElement>('.cluster-panel');
  panels.forEach((el) => {
    const active = el === panel;
    el.hidden = !active;
    el.classList.toggle('cluster-panel--active', active);
  });

  syncList(zoneId);
  document.body.setAttribute('data-current-zone-id', zoneId);
  syncResidentBtn(panel);

  window.dispatchEvent(
    new CustomEvent('stc:cluster-zone-select', { detail: { zoneId, fromPanel: true } }),
  );

  panelCleanup?.();
  panelCleanup = initClusterPanelCarousels(panel);

  const url = clusterUrl(cluster);
  if (opts?.replace) window.history.replaceState({ cluster }, '', url);
  else if (opts?.push) window.history.pushState({ cluster }, '', url);
}

function clusterFromLink(link: HTMLAnchorElement): string | null {
  try {
    const url = new URL(link.href, window.location.href);
    const code = url.searchParams.get('cluster')?.trim().toLowerCase();
    if (code && isClusterId(code)) return code;
  } catch {}
  const fallback = link.dataset.zoneId;
  return fallback && isClusterId(fallback) ? fallback : null;
}

function onListClick(e: MouseEvent): void {
  const link = (e.target as HTMLElement).closest<HTMLAnchorElement>('[data-cluster-spa-link]');
  if (!link) return;
  const cluster = clusterFromLink(link);
  if (!cluster) return;
  e.preventDefault();
  if (cluster === parseCluster()) return;
  showCluster(cluster, { push: true });
}

function onZoneSelect(e: Event): void {
  const detail = (e as CustomEvent).detail;
  if (detail?.fromPanel) return;
  const zoneId = detail?.zoneId;
  if (!zoneId || !isClusterId(zoneId)) return;
  if (zoneId === parseCluster()) return;
  showCluster(zoneId, { push: true });
}

function onPopState(): void {
  showCluster(parseCluster());
}

export function initClusterSpa(): void {
  const root = document.querySelector<HTMLElement>('.clusters-page--spa');
  if (!root) return;

  disposeClusterPanelBootstrapCarousels();

  if (listRoot !== root) {
    listRoot?.removeEventListener('click', onListClick);
    listRoot = root;
    root.addEventListener('click', onListClick);
  }

  if (!globalEventsReady) {
    globalEventsReady = true;
    window.addEventListener('popstate', onPopState);
    window.addEventListener('stc:cluster-zone-select', onZoneSelect);
  }

  showCluster(parseCluster(), { replace: true });
}

export function destroyClusterSpa(): void {
  panelCleanup?.();
  panelCleanup = null;
  listRoot?.removeEventListener('click', onListClick);
  listRoot = null;
}
